import { create } from "zustand";

interface BootstrapState {
  bootstrapped: boolean | null; // null = not yet checked (or last check errored)
  deploymentMode: "SINGLE_ORG" | "MULTI_ORG" | null;
  isChecking: boolean;
  hasChecked: boolean;
  error: boolean; // last check failed (transient) — distinct from "not bootstrapped"

  checkBootstrap: (opts?: { force?: boolean }) => Promise<void>;
  reset: () => void;
}

export const useBootstrapStore = create<BootstrapState>()((set, get) => ({
  bootstrapped: null,
  deploymentMode: null,
  isChecking: false,
  hasChecked: false,
  error: false,

  checkBootstrap: async (opts) => {
    const force = opts?.force ?? false;
    // Prevent duplicate concurrent calls; skip the repeat only when it succeeded.
    if (get().isChecking) return;
    if (get().hasChecked && !force) return;

    set({ isChecking: true, error: false });
    try {
      const res = await fetch("/api/v1/system/bootstrap-status", {
        credentials: "include",
      });
      if (res.ok) {
        const data = (await res.json()) as {
          bootstrapped: boolean;
          deploymentMode: "SINGLE_ORG" | "MULTI_ORG" | null;
        };
        set({
          bootstrapped: data.bootstrapped,
          deploymentMode: data.deploymentMode,
          isChecking: false,
          hasChecked: true,
          error: false,
        });
      } else {
        // S-084: do NOT assume "not bootstrapped" on a server error — that would
        // show the first-run Setup Wizard on an already-configured instance and
        // strand every user (including vendors following an emailed link). Flag a
        // recoverable error and leave `bootstrapped` unknown so the guard offers Retry.
        set({ isChecking: false, hasChecked: true, error: true });
      }
    } catch {
      // Network error — same as above: recoverable, not "wipe and show wizard".
      set({ isChecking: false, hasChecked: true, error: true });
    }
  },

  reset: () =>
    set({ bootstrapped: null, deploymentMode: null, isChecking: false, hasChecked: false, error: false }),
}));
